import React, { useState, useEffect } from 'react';
import { Check, Clock } from 'lucide-react';

// ============================================
// HELPER: Convert 24-hour time to 12-hour format
// ============================================
const formatTime12Hour = (timeStr) => {
  if (!timeStr || timeStr === 'All day') return 'All day';
  
  try {
    // Handle various formats: "17:00", "17:00 PM", "5:00 PM", etc.
    const cleanTime = timeStr.replace(/\s*(AM|PM|am|pm)\s*/gi, '').trim();
    const [hours, minutes] = cleanTime.split(':').map(num => parseInt(num));
    
    if (isNaN(hours)) return timeStr;
    
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    const displayMinutes = minutes ? `:${minutes.toString().padStart(2, '0')}` : ':00';
    
    return `${displayHour}${displayMinutes} ${ampm}`;
  } catch (error) {
    console.error('Time format error:', error, 'Input:', timeStr);
    return timeStr;
  }
};

// ============================================
// HELPER: Save calendar to Firestore
// ============================================
const saveCalendarToFirestore = async (calendar, firestoreService) => {
  if (!firestoreService?.isReady?.()) {
    console.warn('⚠️ Firestore not ready');
    return false;
  }
  
  try {
    await firestoreService.updateUserData({
      calendar: calendar,
      calendarUpdatedAt: new Date().toISOString()
    });
    console.log('✅ Calendar saved to Firestore');
    return true;
  } catch (error) {
    console.error('❌ Failed to save calendar:', error);
    return false;
  }
};

// ============================================
// MAIN COMPONENT
// ============================================
export const AnimatedScheduleDisplay = ({
  date = new Date(), // ✅ ADD DEFAULT VALUE
  dateKey,
  events,
  tasksByDate,
  setTasksByDate,
  firestoreService,
  dateRange = null,
  isMultiDay = false
}) => {
  const [displayedEvents, setDisplayedEvents] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [completionStates, setCompletionStates] = useState({});

  // ============================================
  // INITIAL LOAD: Set up events and their completion states
  // ============================================
useEffect(() => {
  console.log('🎬 AnimatedScheduleDisplay: Initial load', events.length, 'events');
  
  setIsAnimating(true);
  setDisplayedEvents([]);
  
  // ✅ FIX: Ensure all events have dateKey set
  const eventsWithDateKey = events.map(event => ({
    ...event,
    dateKey: event.dateKey || dateKey // Use provided dateKey as fallback
  }));
  
  // Build completion state map
  const initialStates = {};
  eventsWithDateKey.forEach((event, idx) => {
    const key = `${event.dateKey}-${event.text}-${event.time}`;
    initialStates[key] = event.completed || false;
  });
  setCompletionStates(initialStates);

  // Animate events in
  eventsWithDateKey.forEach((event, index) => {
    setTimeout(() => {
      setDisplayedEvents(prev => [...prev, event]);
    }, index * 80);
  });

  setTimeout(() => {
    setIsAnimating(false);
  }, events.length * 80 + 300);
}, [dateKey, dateRange, events]);
  // ============================================
  // REAL-TIME SYNC: Watch for external changes to tasksByDate
  // ============================================
useEffect(() => {
  console.log('🔄 Syncing with tasksByDate changes');
  
  // ✅ FIX: Skip sync if displayedEvents is empty (prevents errors on mount)
  if (displayedEvents.length === 0) {
    return;
  }
  
  // Update displayed events with fresh data from tasksByDate
  const syncedEvents = displayedEvents.map(displayEvent => {
    // ✅ FIX: Use dateKey or fall back to searching all dates
    const eventDateKey = displayEvent.dateKey || dateKey;
    const dateEvents = tasksByDate[eventDateKey] || [];
    
    // Find matching event by text and time
    const matchingEvent = dateEvents.find(e => 
      e.text === displayEvent.text && 
      e.time === displayEvent.time
    );
    
    if (matchingEvent) {
      return {
        ...matchingEvent,
        dateKey: eventDateKey, // ✅ Ensure dateKey is always set
        displayDate: displayEvent.displayDate
      };
    }
    
    return {
      ...displayEvent,
      dateKey: eventDateKey // ✅ Ensure dateKey is always set
    };
  });
  
  // Check if completion states changed
  const hasCompletionChanges = syncedEvents.some((event, idx) => {
    const key = `${event.dateKey}-${event.text}-${event.time}`;
    return completionStates[key] !== event.completed;
  });
  
  if (hasCompletionChanges) {
    console.log('✅ Detected completion changes, updating display');
    setDisplayedEvents(syncedEvents);
    
    // Update completion states
    const newStates = {};
    syncedEvents.forEach(event => {
      const key = `${event.dateKey}-${event.text}-${event.time}`;
      newStates[key] = event.completed || false;
    });
    setCompletionStates(newStates);
  }
}, [tasksByDate, displayedEvents, dateKey, completionStates]);

  // ============================================
  // TOGGLE COMPLETION
  // ============================================
const handleToggleComplete = async (event, displayIndex) => {
  // ✅ FIX: Ensure dateKey exists, fallback to dateKey prop
  const eventDateKey = event.dateKey || dateKey;
  const { text, time } = event;
  const eventKey = `${eventDateKey}-${text}-${time}`;
  
  console.log('🔄 Toggling completion:', {
    dateKey: eventDateKey,
    title: text,
    time: time,
    currentState: event.completed
  });

  // ✅ FIX: Validate dateKey exists
  if (!eventDateKey) {
    console.error('❌ No dateKey available for event:', event);
    return;
  }

  // Get events for this date
  const dateEvents = tasksByDate[eventDateKey] || [];
  
  // Find the actual index in tasksByDate
  const actualIndex = dateEvents.findIndex(e => 
    e.text === text && e.time === time
  );
  
  if (actualIndex === -1) {
    console.error('❌ Event not found in tasksByDate:', {
      dateKey: eventDateKey,
      availableEvents: dateEvents.length,
      searchingFor: { text, time }
    });
    return;
  }

  const newCompletedStatus = !event.completed;

  // ✅ STEP 1: Optimistic UI update
  setDisplayedEvents(prev =>
    prev.map((e, idx) =>
      idx === displayIndex
        ? { ...e, completed: newCompletedStatus, dateKey: eventDateKey }
        : e
    )
  );
  setCompletionStates(prev => ({
    ...prev,
    [eventKey]: newCompletedStatus
  }));

  // ✅ STEP 2: Update tasksByDate state
  const updatedCalendar = {
    ...tasksByDate,
    [eventDateKey]: dateEvents.map((e, idx) =>
      idx === actualIndex
        ? { ...e, completed: newCompletedStatus }
        : e
    )
  };

  console.log('📝 Updating tasksByDate:', {
    dateKey: eventDateKey,
    index: actualIndex,
    newStatus: newCompletedStatus,
    updatedCalendar: Object.keys(updatedCalendar).length
  });
// ✅ STEP 2.5: Update parent state immediately
  setTasksByDate(updatedCalendar);

  // ✅ STEP 3: Save to Firestore
    const saved = await saveCalendarToFirestore(updatedCalendar, firestoreService);

    if (!saved) {
      console.error('❌ Failed to save to Firestore, reverting...');
      
      // Revert optimistic update
      setDisplayedEvents(prev =>
        prev.map((e, idx) =>
          idx === displayIndex
            ? { ...e, completed: !newCompletedStatus }
            : e
        )
      );
      
      setCompletionStates(prev => ({
        ...prev,
        [eventKey]: !newCompletedStatus
      }));
    } else {
      console.log('✅ Completion toggled successfully');
    }
  };

  // ============================================
  // GROUP EVENTS (for multi-day view)
  // ============================================
  const groupedEvents = isMultiDay 
    ? displayedEvents.reduce((acc, event) => {
        const dateLabel = event.displayDate || event.dateKey;
        if (!acc[dateLabel]) acc[dateLabel] = [];
        acc[dateLabel].push(event);
        return acc;
      }, {})
    : { [dateRange || 'Events']: displayedEvents };

  return (
    <div className="schedule-display-container">
      {/* HEADER */}
<div className="schedule-header">
        <div className="schedule-info">
          <h3>Your Schedule</h3>
          <p>{dateRange || (date ? date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          }) : 'Schedule')}</p>
        </div>
        <div className="schedule-count">
          {displayedEvents.length}
        </div>
      </div>

      {/* EVENTS LIST */}
      <div className="schedule-events">
        {Object.entries(groupedEvents).map(([dateLabel, dateEvents]) => (
          <div key={dateLabel} className="schedule-date-group">
            {isMultiDay && (
              <div className="schedule-date-label">{dateLabel}</div>
            )}
            
            {dateEvents.map((event, index) => {
              const globalIndex = displayedEvents.findIndex(e => 
                e.text === event.text && 
                e.time === event.time && 
                e.dateKey === event.dateKey
              );
              
              const eventKey = `${event.dateKey}-${event.text}-${event.time}`;
              const isCompleted = completionStates[eventKey] || false;
              
              return (
                <div
                  key={`${event.dateKey}-${index}-${event.text}-${event.time}`}
                  className="schedule-event"
                  style={{
                    animation: `slideIn 0.3s ease ${globalIndex * 80}ms backwards`,
                  }}
                >
                  <div className={`event-card ${isCompleted ? 'completed' : ''} ${event.isNew ? 'new-event' : ''}`}>
                    
                    <div className="event-content">
                      {/* TIME - 12-HOUR FORMAT */}
                      {event.time && (
                        <div className="event-time">
                          <Clock size={12} />
                          <span>{formatTime12Hour(event.time)}</span>
                        </div>
                      )}

                      {/* TITLE */}
                      <p className="event-title">{event.text}</p>

                      {/* DESCRIPTION */}
                      {event.description && (
                        <p className="event-description">{event.description}</p>
                      )}
                      
                      {/* RECURRING BADGE */}
                      {event.isRecurring && (
                        <div className="recurring-badge">
                          🔄 Recurring
                        </div>
                      )}
                    </div>

                    {/* COMPLETION TOGGLE */}
                    <button
                      onClick={() => handleToggleComplete(event, globalIndex)}
                      className={`complete-btn ${isCompleted ? 'completed' : ''}`}
                      title={isCompleted ? 'Mark incomplete' : 'Mark complete'}
                    >
                      <div className="check-circle">
                        {isCompleted && <Check size={14} strokeWidth={3} />}
                      </div>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* EMPTY STATE */}
      {displayedEvents.length === 0 && !isAnimating && (
        <div className="schedule-empty">
          <p>No tasks scheduled</p>
        </div>
      )}

      {/* STYLES */}
      <style>{`
        .schedule-display-container {
          width: 100%;
          max-width: 100%;
          margin: 8px 0;
          background: rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(20px);
          border-radius: 16px;
          padding: 14px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }

        .schedule-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          gap: 8px;
        }

        .schedule-info {
          flex: 1;
          min-width: 0;
        }

        .schedule-info h3 {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 2px 0;
        }

        .schedule-info p {
          font-size: 11px;
          color: #6b7280;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .schedule-count {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          padding: 4px 10px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }

        .schedule-events {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .schedule-date-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .schedule-date-label {
          font-size: 11px;
          font-weight: 600;
          color: #4b5563;
          margin-bottom: 2px;
          padding-left: 2px;
        }

        .schedule-event {
          width: 100%;
        }

        .event-card {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: rgba(255, 255, 255, 0.7);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.5);
          transition: all 0.3s ease;
        }

        .event-card.new-event {
          animation: highlightNew 0.6s ease;
        }

        .event-card.completed {
          opacity: 0.6;
          background: rgba(244, 245, 247, 0.5);
        }

        .event-card.completed .event-title {
          text-decoration: line-through;
          color: #9ca3af;
        }

        .event-card.completed .event-time {
          opacity: 0.5;
        }

        .event-content {
          flex: 1;
          min-width: 0;
        }

        .event-time {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 10px;
          color: #000;
          font-weight: 600;
          margin-bottom: 4px;
          transition: opacity 0.3s ease;
        }

        .event-title {
          font-size: 13px;
          font-weight: 500;
          color: #4b8ffc;
          margin: 0;
          line-height: 1.3;
          word-break: break-word;
          transition: all 0.3s ease;
        }

        .event-description {
          font-size: 10px;
          color: #6b7280;
          margin: 3px 0 0 0;
          font-style: italic;
          line-height: 1.2;
        }

        .recurring-badge {
          display: inline-block;
          font-size: 9px;
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          padding: 2px 6px;
          border-radius: 6px;
          margin-top: 4px;
        }

        .complete-btn {
          background: none;
          border: none;
          padding: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }

        .complete-btn:hover {
          transform: scale(1.1);
        }

        .complete-btn:active {
          transform: scale(0.95);
        }

        .check-circle {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid #4b8ffc;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          background: transparent;
          color: white;
        }

        .complete-btn.completed .check-circle {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-color: #2563eb;
          animation: completePulse 0.4s ease;
        }

        .schedule-empty {
          text-align: center;
          padding: 20px;
          color: #9ca3af;
          font-size: 12px;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-12px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes highlightNew {
          0% {
            background: rgba(59, 130, 246, 0.2);
            transform: scale(1.02);
          }
          100% {
            background: rgba(255, 255, 255, 0.7);
            transform: scale(1);
          }
        }

        @keyframes completePulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
          }
        }

        /* Mobile specific */
        @media (max-width: 640px) {
          .schedule-display-container {
            padding: 12px;
          }

          .event-card {
            padding: 8px 10px;
          }

          .event-title {
            font-size: 12px;
          }

          .event-time {
            font-size: 9px;
          }

          .check-circle {
            width: 18px;
            height: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedScheduleDisplay;